import { useEffect, useRef, useState } from 'react';

type InputType = 'text' | 'number' | 'email' | 'password';

interface CustomInputProperties {
  inputLabel?: string;
  type: InputType;
  placeholder: string;
  value: string | number | null;
  name: string;
  onChangeValue: (name: string, value: string | number) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  svgIcon?: JSX.Element;
  svgIconClickFunc?: () => void;
  error?: boolean;
  errorMessage?: string;
  autoFocus?: boolean;
}

/**
 * Custom input box
 */
function CustomInput({
  inputLabel,
  type,
  name,
  placeholder,
  value,
  onChangeValue,
  valueRequired,
  readOnly,
  svgIcon,
  autoFocus,
  svgIconClickFunc,
  error,
  errorMessage,
}: CustomInputProperties) {
  const inputReference = useRef<HTMLInputElement>(null);
  const [inputType, setInputType] = useState<InputType>(type);
  /**
   * Handle input change
   */
  const handleValueChange = (changeValue: string | number) => {
    onChangeValue(name, changeValue);
  };
  useEffect(() => {
    if (autoFocus && inputReference.current) {
      inputReference.current.focus(); // Focus the input field if condition is true
    }
  }, [autoFocus]);

  return (
    <div className="custom-input-wrap">
      {inputLabel && (
        <p className={`input-label ${error && 'error'}`}>
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className={`custom-input-container ${error && 'error'}`}>
        <input
          type={type === 'password' ? inputType : type}
          placeholder={placeholder}
          value={value !== null ? value : undefined}
          onChange={(event) => handleValueChange(event.target.value)}
          required={valueRequired}
          readOnly={readOnly}
          ref={inputReference}
        />
        {svgIcon && type !== 'password' && (
          <span role="presentation" onClick={svgIconClickFunc}>
            {svgIcon}
          </span>
        )}
        {(() => {
          let passwordIcon: JSX.Element | null = null; // Initialize the type as either a JSX element or null
          if (type === 'password' && inputType === 'password') {
            passwordIcon = (
              <span role="presentation" onClick={() => setInputType('text')}>
                Icon
              </span>
            );
          } else if (type === 'password' && inputType === 'text') {
            passwordIcon = (
              <span role="presentation" onClick={() => setInputType('password')}>
                Icon
              </span>
            );
          }
          return passwordIcon; // Return the icon or null
        })()}
      </div>
      {error && <p className="custom-input-error-message">{errorMessage}</p>}
    </div>
  );
}

CustomInput.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  readOnly: false,
  svgIcon: null,
  svgIconClickFunc: null,
  error: false,
  errorMessage: '',
  autoFocus: false,
};

export default CustomInput;
